"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export function useSlider(itemsLength: number, autoAdvanceDelay = 4000) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Navigation functions
  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % itemsLength);
  }, [itemsLength]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? itemsLength - 1 : prev - 1));
  }, [itemsLength]);

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  // Reset interval function
  const resetInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    if (!isHovered) {
      intervalRef.current = setInterval(goToNext, autoAdvanceDelay);
    }
  }, [isHovered, goToNext, autoAdvanceDelay]);

  // Auto-advance slides
  useEffect(() => {
    if (!isHovered) {
      intervalRef.current = setInterval(goToNext, autoAdvanceDelay);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isHovered, goToNext, autoAdvanceDelay]);

  // Handle navigation with interval reset
  const handleNext = useCallback(() => {
    goToNext();
    resetInterval();
  }, [goToNext, resetInterval]);

  const handlePrevious = useCallback(() => {
    goToPrevious();
    resetInterval();
  }, [goToPrevious, resetInterval]);

  const handleSlideClick = useCallback(
    (index: number) => {
      goToSlide(index);
      resetInterval();
    },
    [goToSlide, resetInterval],
  );

  return {
    currentIndex,
    isHovered,
    setIsHovered,
    handleNext,
    handlePrevious,
    handleSlideClick,
    goToNext,
    goToPrevious,
    goToSlide,
  };
}
